import { ReactNode, useContext, useEffect } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  Routes,
  useLocation,
  Navigate,
} from "react-router-dom";

import AuthProvider, {
  AuthContext,
} from "./components/Auth/AuthProvider.component";
import Login, { LoginContextEnum } from "./components/Auth/Login";
import LoginCallback from "./components/Auth/OktaLoginCallback";
import SecureRoute from "./components/Auth/SecureRoute.component";
import { Home } from "./components/Home";
import config from "./config";
import {
  CognitoAuthProvider,
  CognitoLoginPage,
  CognitoCallback,
  useCognitoAuth,
} from "frontend/src/components/CognitoAuth";
import { CognitoAuthAdapter } from "./components/Auth/CognitoAuthAdapter";
import { ThemeModeProvider } from "frontend/src/theme/ThemeModeProvider";
import { SnackbarProvider } from "frontend/src/feedback/SnackbarProvider";

function Logout() {
  const { signOut } = useContext(AuthContext);

  useEffect(() => {
    signOut();
  }, [signOut]);

  return <Navigate to="/login" />;
}

function CognitoLogout() {
  const { signOut } = useCognitoAuth();

  useEffect(() => {
    signOut();
  }, [signOut]);

  return <Navigate to="/login" />;
}

const SecureApp = () => {
  const location = useLocation();

  // Check if Cognito config is present
  const useCognito = !!(
    config.POOL_ID() &&
    config.POOL_CLIENT_ID() &&
    config.COGNITO_DOMAIN()
  );

  useEffect(() => {
    const timer1 = setTimeout(() => {
      // make the page scroll to the top after navigating
      window.scrollTo(0, 0);
    }, 0);
    const timer2 = setTimeout(() => {
      // a little trick to set the page title that shows on browser tab
      // also shows up nicely in browser history
      document.title = `Sirius Data Collection - ${
        document.getElementById("react-admin-title")?.innerText ?? ""
      }`;

      const cleanup = () => {
        clearTimeout(timer2);
        clearTimeout(timer1);
      };
      return cleanup;
    }, 1000);
  }, [location.pathname]);

  const messageHandler = (event: MessageEvent) => {
    if (event.data.action === "SEND_AUTH_CREDENTIALS") {
      // event.cognito as obhect of key and value strings
      Object.entries(event.data.cognito as { [key: string]: string }).forEach(
        ([key, value]) => {
          console.log("setting key", key);
          window.localStorage.setItem(key, value);
        }
      );
      window.localStorage.setItem("authState", "signedIn");
      window.localStorage.setItem("dcLoginOption", "externalManager");
    }
  };

  //recieve message from parent window
  useEffect(() => {
    console.log(
      "READY_TO_RECIEVE_MESSAGES",
      window.parent,
      config.PORTAL_URL()
    );
    window.parent.postMessage("READY_TO_RECIEVE_MESSAGES", config.PORTAL_URL());
    window.addEventListener("message", messageHandler);
    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, []);

  // Cognito Auth Flow
  if (useCognito) {
    const cognitoConfig = {
      userPoolId: config.POOL_ID()!,
      clientId: config.POOL_CLIENT_ID()!,
      domain: config.COGNITO_DOMAIN()!,
      region: config.POOL_REGION() || "us-east-1",
      redirectUri: `${window.location.origin}/cognito/callback`,
    };

    return (
      <CognitoAuthProvider config={cognitoConfig}>
        <CognitoAuthAdapter>
          <Routes>
            <Route path="/cognito/callback" element={<CognitoCallback />} />
            <Route
              path="/login/businessApplication"
              element={
                <Login loginContext={LoginContextEnum.businessApplicant} />
              }
            />
            <Route
              path="/login/external"
              element={
                <Login loginContext={LoginContextEnum.externalManager} />
              }
            />
            <Route
              path="/login/internal"
              element={
                <CognitoLoginPage
                  graphqlUri={config.GRAPHQL_API()}
                  title="Partner Login"
                  subtitle="Enter your work email to continue"
                  logoUrl="/sirius_logo.jpg"
                />
              }
            />
            <Route
              path="/login"
              element={
                <CognitoLoginPage
                  graphqlUri={config.GRAPHQL_API()}
                  title="Sign In"
                  subtitle="Enter your email to continue"
                  logoUrl="/sirius_logo.jpg"
                />
              }
            />
            <Route path="/logout" element={<CognitoLogout />} />

            <Route
              path="*"
              element={
                <SecureRoute useCognito={true}>
                  <Home />
                </SecureRoute>
              }
            />
          </Routes>
        </CognitoAuthAdapter>
      </CognitoAuthProvider>
    );
  }

  // Okta Auth Flow (fallback)
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login/okta/callback" element={<LoginCallback />} />
        <Route
          path="/login/businessApplication"
          element={<Login loginContext={LoginContextEnum.businessApplicant} />}
        />
        <Route
          path="/login/external"
          element={<Login loginContext={LoginContextEnum.externalManager} />}
        />
        <Route
          path="/login/internal"
          element={<Login loginContext={LoginContextEnum.rcManager} />}
        />
        <Route
          path="/login"
          element={<Login loginContext={LoginContextEnum.main} />}
        />
        <Route path="/logout" element={<Logout />} />

        <Route
          path="*"
          element={
            <SecureRoute useCognito={false}>
              <Home />
            </SecureRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
};

const routes = createRoutesFromElements(
  <Route path="*" element={<SecureApp />} />
);
const router = createBrowserRouter(routes);

const App = (): ReactNode => {
  return (
    <ThemeModeProvider>
      <SnackbarProvider>
        <RouterProvider router={router} />
      </SnackbarProvider>
    </ThemeModeProvider>
  );
};

export default App;
